import React, { useState, useEffect, useRef } from "react";
import { useAuthContext } from "../lib/authLib";
import { User, get as getUser } from "../lib/userLib";
import {
  Inbox,
  update as updateInbox,
  filteringModes,
  sendStatsDigestEmail,
} from "../lib/inboxLib";
import { Tab, TabGroup, TabList, TabPanels } from "@headlessui/react";
import { Switch } from "@headlessui/react";
import * as charitiesLib from "../lib/charitiesLib";
import * as deletionsLib from "../lib/deletionsLib";
import { useSnackbar } from "../contexts/SnackbarContext";
import CharityPicker from "./charityPicker";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import OnboardingStatsModal from "./onboardingStatsModal";
import TestChallengeModal from "./TestChallengeModal";

// Custom hook for debouncing values
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(timer);
    };
  }, [value, delay]);

  return debouncedValue;
}

const Settings: React.FC = () => {
  const { user: authUser } = useAuthContext();
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedInbox, setSelectedInbox] = useState<Inbox | null>(null);
  const [_charities, setCharities] = useState<charitiesLib.Charity[]>([]);
  const [minDonation, setMinDonation] = useState(0);
  const [challengeMessage, setChallengeMessage] = useState("");
  const [bypassMessage, setBypassMessage] = useState("");
  const [displayName, setDisplayName] = useState("");
  const isInitializingRef = useRef(true);
  const initialValuesRef = useRef({
    displayName: "",
    minDonation: 0,
    challengeMessage: "",
    bypassMessage: "",
  });
  const { showSnackbar } = useSnackbar();
  const [showCharityPicker, setShowCharityPicker] = useState(false);
  const [showOnboardingStats, setShowOnboardingStats] = useState(false);
  const [showTestChallenge, setShowTestChallenge] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { inboxId } = useParams();
  const challengeTextareaRef = useRef<HTMLTextAreaElement>(null);
  const cursorPositionRef = useRef<number>(0);

  //This is manually kept in sync with the default challenge message in the backend
  const DEFAULT_CHALLENGE_MESSAGE = `Hi there!
  
I'm using Cobutler to help reduce noise in my inbox and help me focus on what's most important.

I'd love to challenge you to {c:make a charitable donation} to get our conversation started. If we know each other and you'd like to ask to skip the donation, you can {b:click here}.

{firstName}`;

  // Get userId from query params or use authenticated user's id
  const queryParams = new URLSearchParams(location.search);
  const userIdFromParams = queryParams.get("userId");
  const userId = userIdFromParams || authUser?.id;

  // Check if user is an admin (viewing another user's settings)
  const isAdmin = userIdFromParams && userIdFromParams !== authUser?.id;

  // Debounced values
  const debouncedDisplayName = useDebounce(displayName, 500);
  const debouncedMinDonation = useDebounce(minDonation, 500);
  const debouncedChallengeMessage = useDebounce(challengeMessage, 500);
  const debouncedBypassMessage = useDebounce(bypassMessage, 500);

  useEffect(() => {
    const fetchData = async () => {
      if (!userId) return;

      try {
        const userData = await getUser(userId);
        setUser(userData);

        if (!userData.inboxes || userData.inboxes.length === 0) {
          setSelectedInbox(null);
        } else {
          if (location.pathname === "/settings") {
            const userIdParam = isAdmin ? `?userId=${userId}` : "";
            navigate(
              `/settings/inbox/${userData.inboxes[0].email}${userIdParam}`,
              { replace: true },
            );
            return;
          }

          if (location.pathname === "/settings/account") {
            setSelectedInbox(null);
          } else if (inboxId) {
            let targetInbox: Inbox | undefined;

            targetInbox = userData.inboxes.find(
              (inbox) => inbox.email === inboxId,
            );

            if (!targetInbox) {
              const index = parseInt(inboxId);
              if (
                !isNaN(index) &&
                index >= 0 &&
                index < userData.inboxes.length
              ) {
                targetInbox = userData.inboxes[index];
              }
            }

            setSelectedInbox(targetInbox || userData.inboxes[0]);
          } else {
            setSelectedInbox(userData.inboxes[0]);
          }
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
        showSnackbar("Failed to load user data", "error");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [userId, inboxId, location.pathname, isAdmin, navigate]);

  useEffect(() => {
    const fetchCharities = async () => {
      try {
        const data = await charitiesLib.getAll();
        setCharities(data);
      } catch (error) {
        console.error("Failed to fetch charities:", error);
        showSnackbar("Failed to load charities", "error");
      }
    };

    fetchCharities();
  }, []);

  const handleInboxChange = (inbox: Inbox) => {
    setSelectedInbox(inbox);
    // Only retain userId in the URL if user is an admin
    const userIdParam = isAdmin ? `?userId=${userId}` : "";
    navigate(`/settings/inbox/${inbox.email}${userIdParam}`);
  };

  const handleSaveInboxSettings = async (
    inboxId: string,
    updateData: Partial<Inbox>,
  ) => {
    try {
      await updateInbox(inboxId, updateData);
      showSnackbar("Settings saved successfully", "success");

      // Refresh user data to get updated inbox
      if (userId) {
        const userData = await getUser(userId);
        setUser(userData);
        const updatedInbox = userData.inboxes.find((i) => i.id === inboxId);
        if (updatedInbox) {
          setSelectedInbox(updatedInbox);
        }
      }
    } catch (error) {
      console.error("Failed to save settings:", error);
      showSnackbar("Failed to save settings", "error");
    }
  };

  // Update local state when selected inbox changes
  useEffect(() => {
    if (selectedInbox) {
      isInitializingRef.current = true;
      const newDisplayName = selectedInbox.displayName || "";
      // Convert cents to dollars for display
      const newMinDonation = (selectedInbox.minimumDonation || 200) / 100;
      const newChallengeMessage = selectedInbox.challengeMessage ?? "";
      const newBypassMessage = selectedInbox.bypassMessage ?? "";

      // Store initial values
      initialValuesRef.current = {
        displayName: newDisplayName,
        minDonation: newMinDonation,
        challengeMessage: newChallengeMessage,
        bypassMessage: newBypassMessage,
      };

      setDisplayName(newDisplayName);
      setMinDonation(newMinDonation);
      setChallengeMessage(newChallengeMessage);
      setBypassMessage(newBypassMessage);
      isInitializingRef.current = false;
    }
  }, [selectedInbox]);

  // Save debounced values
  useEffect(() => {
    if (
      selectedInbox &&
      !isInitializingRef.current &&
      debouncedDisplayName !== initialValuesRef.current.displayName
    ) {
      handleSaveInboxSettings(selectedInbox.id, {
        displayName: debouncedDisplayName,
      });
    }
  }, [debouncedDisplayName]);

  useEffect(() => {
    if (
      selectedInbox &&
      !isInitializingRef.current &&
      debouncedMinDonation !== initialValuesRef.current.minDonation
    ) {
      // Convert dollars to cents (integer) for API
      const minDonationCents = Math.round(debouncedMinDonation * 100);
      handleSaveInboxSettings(selectedInbox.id, {
        minimumDonation: minDonationCents,
        defaultDonation: minDonationCents * 2,
      });
    }
  }, [debouncedMinDonation]);

  useEffect(() => {
    if (
      selectedInbox &&
      !isInitializingRef.current &&
      debouncedChallengeMessage !== initialValuesRef.current.challengeMessage
    ) {
      handleSaveInboxSettings(selectedInbox.id, {
        challengeMessage: debouncedChallengeMessage,
      });
    }
  }, [debouncedChallengeMessage]);

  useEffect(() => {
    if (
      selectedInbox &&
      !isInitializingRef.current &&
      debouncedBypassMessage !== initialValuesRef.current.bypassMessage
    ) {
      handleSaveInboxSettings(selectedInbox.id, {
        bypassMessage: debouncedBypassMessage,
      });
    }
  }, [debouncedBypassMessage]);

  const insertTemplate = (template: string) => {
    if (challengeTextareaRef.current) {
      const start = cursorPositionRef.current;
      const newText =
        challengeMessage.slice(0, start) +
        template +
        challengeMessage.slice(start);
      setChallengeMessage(newText);
      // Set timeout to allow React to update the textarea value
      setTimeout(() => {
        if (challengeTextareaRef.current) {
          challengeTextareaRef.current.focus();
          const newPosition = start + template.length;
          challengeTextareaRef.current.setSelectionRange(
            newPosition,
            newPosition,
          );
        }
      }, 0);
    }
  };

  const handleDeleteAccount = async (immediate: boolean = false) => {
    if (!userId) return;

    const confirmMessage = immediate
      ? "Are you sure you want to delete this account immediately? This action cannot be undone and will permanently delete all data."
      : "Are you sure you want to schedule this account for deletion? This will schedule the account to be deleted in 90 days.";

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      const result = await deletionsLib.deleteUser(userId, immediate);
      showSnackbar(result.message, "success");

      // Wait a second then reload the page to kick them back to login
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (error) {
      console.error("Error deleting account:", error);
      showSnackbar("Failed to delete account", "error");
    }
  };

  const handleDeleteInbox = async (inboxId: string) => {
    if (
      !window.confirm(
        "Are you sure you want to schedule this inbox for deletion? This will schedule the inbox to be deleted in 90 days.",
      )
    ) {
      return;
    }

    try {
      const result = await deletionsLib.deleteInbox(inboxId, false); // Always scheduled (90 days)
      showSnackbar(result.message, "success");

      // Refresh user data
      if (userId) {
        const userData = await getUser(userId);
        setUser(userData);

        // If no inboxes left, go to account tab
        if (userData.inboxes.length === 0) {
          setSelectedInbox(null);
          const userIdParam = isAdmin ? `?userId=${userId}` : "";
          navigate(`/settings${userIdParam}`);
        } else {
          // Otherwise select the first inbox
          setSelectedInbox(userData.inboxes[0]);
          const userIdParam = isAdmin ? `?userId=${userId}` : "";
          navigate(
            `/settings/inbox/${userData.inboxes[0].email}${userIdParam}`,
          );
        }
      }
    } catch (error) {
      console.error("Error deleting inbox:", error);
      showSnackbar("Failed to delete inbox", "error");
    }
  };

  const handleSendStatsDigest = async () => {
    if (!selectedInbox) return;

    try {
      await sendStatsDigestEmail(selectedInbox.id, true);
      showSnackbar("Stats summary digest requested", "success");
    } catch (error) {
      console.error("Error sending stats digest:", error);
      showSnackbar("Failed to send stats summary digest", "error");
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  // Calculate the selected tab index
  const selectedTabIndex = !inboxId
    ? 0
    : selectedInbox && user?.inboxes
      ? user.inboxes.findIndex((i) => i.id === selectedInbox.id) + 1 // +1 because Account tab is at index 0
      : 0;

  return (
    <div className="container mx-auto py-6 px-4">
      <h1 className="text-2xl font-bold mb-6 dark:text-white">
        Settings
        {isAdmin && user?.email && (
          <span className="ml-4 text-base font-normal text-red-500 dark:text-red-300">
            (you are an admin acting on behalf of {user.email}.{" "}
            <a
              href="/settings"
              className="underline text-red-500 dark:text-red-300 hover:text-red-600 dark:hover:text-red-400"
            >
              Click here to go to your own settings
            </a>
            )
          </span>
        )}
      </h1>

      <TabGroup
        selectedIndex={selectedTabIndex}
        onChange={(index) => {
          const userIdParam = isAdmin ? `?userId=${userId}` : "";
          if (index === 0) {
            setSelectedInbox(null);
            navigate(`/settings/account${userIdParam}`);
          } else if (user?.inboxes && user.inboxes[index - 1]) {
            handleInboxChange(user.inboxes[index - 1]);
          }
        }}
      >
        <TabList className="flex space-x-1 rounded-xl bg-blue-900/20 p-1">
          <Tab
            className={({ selected }) =>
              `w-full rounded-lg py-2.5 text-sm font-medium leading-5
              ${
                selected
                  ? "bg-white text-blue-700 shadow dark:bg-gray-800 dark:text-white"
                  : "text-blue-100 hover:bg-white/[0.12] hover:text-white"
              }`
            }
          >
            Account
          </Tab>
          {user?.inboxes.map((inbox) => (
            <Tab
              key={inbox.id}
              className={({ selected }) =>
                `w-full rounded-lg py-2.5 text-sm font-medium leading-5
                ${
                  selected
                    ? "bg-white text-blue-700 shadow dark:bg-gray-800 dark:text-white"
                    : "text-blue-100 hover:bg-white/[0.12] hover:text-white"
                }`
              }
            >
              {inbox.displayName || inbox.email} - {inbox.email}
            </Tab>
          ))}
        </TabList>

        <TabPanels className="mt-6">
          {/* Account Settings Panel */}
          <Tab.Panel className="rounded-xl bg-white dark:bg-gray-800 p-6 shadow-lg">
            <h2 className="text-xl font-semibold mb-4 dark:text-white">
              Account Settings
            </h2>
            <div className="space-y-4">
              <p className="text-gray-600 dark:text-gray-400">
                Account settings and subscription management coming soon.
              </p>

              {/* Delete Account Section */}
              <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-semibold mb-3 text-red-600 dark:text-red-400">
                  Danger Zone
                </h3>
                <div className="flex gap-3">
                  <button
                    onClick={() => handleDeleteAccount(false)}
                    className="px-3 py-1.5 bg-orange-600 text-white text-sm rounded hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500"
                    title="Schedule account deletion in 90 days (can be cancelled)"
                  >
                    Schedule Deletion
                  </button>
                  <button
                    onClick={() => handleDeleteAccount(true)}
                    className="px-3 py-1.5 bg-red-600 text-white text-sm rounded hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
                    title="Permanently delete account immediately (cannot be undone)"
                  >
                    Delete Immediately
                  </button>
                </div>
              </div>
            </div>
          </Tab.Panel>

          {/* Inbox Settings Panels */}
          {user?.inboxes.map((inbox) => (
            <Tab.Panel
              key={inbox.id}
              className="rounded-xl bg-white dark:bg-gray-800 p-6 shadow-lg"
            >
              <h2 className="text-xl font-semibold mb-4 dark:text-white flex items-center">
                {inbox.profilePicture ? (
                  <img
                    src={inbox.profilePicture}
                    alt={`${inbox.displayName || inbox.email} profile`}
                    className="w-8 h-8 rounded-full mr-2"
                  />
                ) : (
                  <div className="w-8 h-8 rounded-full mr-2 bg-gray-300 flex items-center justify-center text-gray-600 text-xl font-semibold">
                    {(inbox.displayName || inbox.email || "?")
                      .charAt(0)
                      .toUpperCase()}
                  </div>
                )}
                <span>
                  Inbox Settings - {inbox.email}{" "}
                  <a
                    href={`${process.env.REACT_APP_CHALLENGE_APP_URL}/i/${inbox.id}`}
                    target="_blank"
                    className="text-blue-600 dark:text-blue-400 hover:underline text-xs"
                    rel="noreferrer"
                  >
                    View your static landing page for this inbox
                  </a>
                </span>
              </h2>

              {/* Two-Column Grid Layout */}
              <div className="grid lg:grid-cols-2 gap-6">
                {/* Left Column */}
                <div className="space-y-6">
                  {/* Display Name */}
                  <div>
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 block">
                      Display Name
                    </label>
                    <input
                      type="text"
                      value={displayName}
                      onChange={(e) => setDisplayName(e.target.value)}
                      placeholder="Custom name (optional)"
                      className="w-full text-sm px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                      maxLength={30}
                    />
                    {!displayName && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Defaults to {inbox.email}
                      </p>
                    )}
                  </div>

                  {/* Filtering Mode */}
                  <div>
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 block">
                      Filtering Mode
                    </label>
                    <select
                      value={inbox.filteringMode}
                      onChange={(e) =>
                        handleSaveInboxSettings(inbox.id, {
                          filteringMode: e.target.value as filteringModes,
                        })
                      }
                      className="w-full text-sm px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                    >
                      <option value={filteringModes.DISABLED}>Disabled</option>
                      <option value={filteringModes.FILTERING}>
                        Filtering
                      </option>
                      <option value={filteringModes.SIMULATING}>
                        Simulating
                      </option>
                    </select>
                  </div>

                  {/* Enable Challenges Toggle */}
                  <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    <div>
                      <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Enable Challenges
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Automatically ask unknown senders to donate to your
                        charity in order to get their email delivered to your
                        mailbox
                      </p>
                    </div>
                    <Switch
                      checked={inbox.challengeEnabled}
                      onChange={(checked) =>
                        handleSaveInboxSettings(inbox.id, {
                          challengeEnabled: checked,
                        })
                      }
                      className={`${
                        inbox.challengeEnabled ? "bg-blue-600" : "bg-gray-200"
                      } relative inline-flex h-6 w-11 items-center rounded-full transition-colors
                      focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2`}
                    >
                      <span className="sr-only">Enable challenges</span>
                      <span
                        className={`${
                          inbox.challengeEnabled
                            ? "translate-x-6"
                            : "translate-x-1"
                        } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                      />
                    </Switch>
                  </div>

                  {/* Thread Blessing Toggle */}
                  <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    <div>
                      <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Smart Thread Filtering
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        When you or a known contact participates in a thread, all
                        future replies stay in your inbox regardless of sender
                      </p>
                    </div>
                    <Switch
                      checked={inbox.threadBlessingEnabled}
                      onChange={(checked) =>
                        handleSaveInboxSettings(inbox.id, {
                          threadBlessingEnabled: checked,
                        })
                      }
                      className={`${
                        inbox.threadBlessingEnabled
                          ? "bg-blue-600"
                          : "bg-gray-200"
                      } relative inline-flex h-6 w-11 items-center rounded-full transition-colors
                      focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2`}
                    >
                      <span className="sr-only">
                        Enable smart thread filtering
                      </span>
                      <span
                        className={`${
                          inbox.threadBlessingEnabled
                            ? "translate-x-6"
                            : "translate-x-1"
                        } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                      />
                    </Switch>
                  </div>
                </div>

                {/* Right Column */}
                <div className="space-y-6">
                  {/* Donation Amount */}
                  <div>
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 block">
                      Minimum Donation Amount
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-2 text-sm text-gray-500 dark:text-gray-400">
                        $
                      </span>
                      <input
                        type="number"
                        min="0"
                        max="500"
                        step="1"
                        value={minDonation}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === "") {
                            setMinDonation(0);
                            return;
                          }
                          const numValue = parseFloat(value);
                          if (!isNaN(numValue)) {
                            const rounded = Math.round(numValue * 100) / 100;
                            setMinDonation(rounded);
                            if (rounded > 500) {
                              showSnackbar(
                                "Maximum donation amount is $500",
                                "warning",
                              );
                            }
                          }
                        }}
                        onBlur={(e) => {
                          const numValue = parseFloat(e.target.value);
                          if (!isNaN(numValue)) {
                            const rounded = Math.min(
                              Math.round(numValue * 100) / 100,
                              500,
                            );
                            setMinDonation(rounded);
                          }
                        }}
                        className="w-20 text-sm px-3 py-2 pl-6 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                      />
                    </div>

                    {/* Donation Preview Chips */}
                    <div className="flex gap-2 mt-3 items-end">
                      <div className="px-3 py-1.5 bg-gray-200 dark:bg-gray-600 rounded-md text-sm">
                        ${minDonation.toFixed(2)}
                      </div>
                      <div className="flex flex-col items-center">
                        <span className="text-[10px] font-semibold text-blue-400 dark:text-blue-300 mb-0.5 tracking-widest">
                          DEFAULT
                        </span>
                        <div className="px-3 py-1.5 bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 rounded-md text-sm font-medium">
                          ${(minDonation * 2).toFixed(2)}
                        </div>
                      </div>
                      <div className="px-3 py-1.5 bg-gray-200 dark:bg-gray-600 rounded-md text-sm">
                        ${(minDonation * 4).toFixed(2)}
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                      Your challenge page will suggest these three donation
                      amounts
                    </p>
                    {minDonation > 500 && (
                      <p className="text-xs text-red-500 dark:text-red-400 mt-1">
                        Maximum donation amount is $500
                      </p>
                    )}
                  </div>

                  {/* Selected Charity */}
                  <div>
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 block">
                      Selected Charity
                    </label>
                    {inbox.charity ? (
                      <div className="flex items-center space-x-4 p-4 bg-white dark:bg-gray-700 rounded-lg border border-gray-300 dark:border-gray-600">
                        {inbox.charity.logoUrl && (
                          <div className="h-12 w-12 rounded-full bg-white dark:bg-gray-800 p-1 flex-shrink-0">
                            <img
                              src={inbox.charity.logoUrl}
                              alt={`${inbox.charity.name} logo`}
                              className="w-full h-full object-contain rounded-full"
                            />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <h3 className="font-medium text-sm dark:text-white truncate">
                            {inbox.charity.name}
                          </h3>
                          {inbox.charity.blurb && (
                            <p className="text-gray-600 dark:text-gray-400 text-xs mt-0.5 line-clamp-2">
                              {inbox.charity.blurb}
                            </p>
                          )}
                        </div>
                        <button
                          onClick={() => setShowCharityPicker(true)}
                          className="px-3 py-1.5 bg-blue-500 text-white text-sm rounded hover:bg-blue-600 flex-shrink-0"
                        >
                          Change
                        </button>
                      </div>
                    ) : (
                      <div className="flex justify-center p-8 bg-gray-50 dark:bg-gray-700 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600">
                        <button
                          onClick={() => setShowCharityPicker(true)}
                          className="px-4 py-2 bg-blue-500 text-white text-sm rounded hover:bg-blue-600"
                        >
                          Select a Charity
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Challenge Message Section - Full Width */}
                <div className="lg:col-span-2 border-t border-gray-200 dark:border-gray-700 pt-6">
                  {/* Header Row */}
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Challenge Message
                    </h3>
                    <button
                      onClick={() => {
                        setChallengeMessage(DEFAULT_CHALLENGE_MESSAGE);
                        setTimeout(() => {
                          if (challengeTextareaRef.current) {
                            challengeTextareaRef.current.focus();
                            const length = DEFAULT_CHALLENGE_MESSAGE.length;
                            challengeTextareaRef.current.setSelectionRange(
                              length,
                              length,
                            );
                          }
                        }, 0);
                      }}
                      className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                    >
                      Reset to default
                    </button>
                  </div>

                  {/* Content Grid */}
                  <div className="grid lg:grid-cols-3 gap-4">
                    {/* Left Side - Template Buttons + Textarea (2 columns) */}
                    <div className="lg:col-span-2 space-y-3">
                      {/* Template Variable Buttons */}
                      <div className="flex flex-wrap gap-2 text-xs">
                        <button
                          onClick={() =>
                            insertTemplate("{c:click here to make a donation}")
                          }
                          className="px-2 py-1 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded text-gray-700 dark:text-gray-300"
                        >
                          + Challenge link
                        </button>
                        <button
                          onClick={() =>
                            insertTemplate("{b:click here to request a bypass}")
                          }
                          className="px-2 py-1 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded text-gray-700 dark:text-gray-300"
                        >
                          + Bypass link
                        </button>
                        <button
                          onClick={() => insertTemplate("{senderName}")}
                          className="px-2 py-1 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded text-gray-700 dark:text-gray-300"
                        >
                          + Sender name
                        </button>
                        <button
                          onClick={() => insertTemplate("{firstName}")}
                          className="px-2 py-1 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded text-gray-700 dark:text-gray-300"
                        >
                          + Your first name
                        </button>
                        <button
                          onClick={() => insertTemplate("{charityName}")}
                          className="px-2 py-1 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded text-gray-700 dark:text-gray-300"
                        >
                          + Charity name
                        </button>
                      </div>

                      {/* Textarea */}
                      <textarea
                        ref={challengeTextareaRef}
                        value={challengeMessage}
                        onChange={(e) => setChallengeMessage(e.target.value)}
                        onSelect={(e) => {
                          cursorPositionRef.current = (
                            e.target as HTMLTextAreaElement
                          ).selectionStart;
                        }}
                        className="w-full h-32 text-sm px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white font-mono resize-none"
                      />
                    </div>

                    {/* Right Side - Live Preview (1 column) */}
                    <div className="lg:col-span-1">
                      <div className="bg-gray-50 dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg p-4 h-full">
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
                            Live Preview
                          </span>
                          <button
                            onClick={() => setShowTestChallenge(true)}
                            className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                          >
                            Send test
                          </button>
                        </div>
                        <div className="text-sm whitespace-pre-line break-words">
                          {challengeMessage
                            ? challengeMessage
                                .replace(/{senderName}/g, "John Smith")
                                .replace(/{firstName}/g, user?.firstName || "")
                                .replace(
                                  /{charityName}/g,
                                  inbox.charity?.name || "Selected Charity",
                                )
                                .replace(
                                  /{c:([^}]*)}/g,
                                  '<a href="#" class="text-blue-600 dark:text-blue-400 hover:underline">$1</a>',
                                )
                                .replace(
                                  /{b:([^}]*)}/g,
                                  '<a href="#" class="text-blue-600 dark:text-blue-400 hover:underline">$1</a>',
                                )
                                .split(/(<a[^>]*>.*?<\/a>)/g)
                                .map((part, i) =>
                                  part.startsWith("<a") ? (
                                    <span
                                      key={i}
                                      dangerouslySetInnerHTML={{ __html: part }}
                                    />
                                  ) : (
                                    <span key={i}>{part}</span>
                                  ),
                                )
                            : "Preview will appear here..."}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-6 mt-6">
                {/* Stats Summary Section */}
                <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
                  <h3 className="text-lg font-semibold mb-3 text-gray-900 dark:text-white">
                    Stats Summary
                  </h3>
                  <div className="bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                      Request a stats summary digest email for this inbox.
                    </p>
                    <button
                      onClick={handleSendStatsDigest}
                      className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      Send me a stats summary digest
                    </button>
                  </div>
                </div>

                {/* Delete Inbox Section */}
                <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
                  <h3 className="text-lg font-semibold mb-3 text-red-600 dark:text-red-400">
                    Danger Zone
                  </h3>
                  <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 dark:text-white mb-2">
                      Delete inbox permanently
                    </h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                      This will delete the inbox permanently and all associated
                      data (messages, rules, etc.).
                    </p>
                    <button
                      onClick={() => handleDeleteInbox(inbox.id)}
                      className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
                    >
                      Delete inbox
                    </button>
                  </div>
                </div>
              </div>
            </Tab.Panel>
          ))}
        </TabPanels>
      </TabGroup>

      {/* Charity Picker Modal */}
      {selectedInbox && (
        <CharityPicker
          isOpen={showCharityPicker}
          onClose={() => setShowCharityPicker(false)}
          onSave={handleSaveInboxSettings}
          inbox={selectedInbox}
        />
      )}

      {/* Onboarding Stats Modal */}
      {selectedInbox && (
        <OnboardingStatsModal
          isOpen={showOnboardingStats}
          onClose={() => setShowOnboardingStats(false)}
          inboxId={selectedInbox.id}
        />
      )}

      {/* Test Challenge Modal */}
      {selectedInbox && (
        <TestChallengeModal
          isOpen={showTestChallenge}
          onClose={() => setShowTestChallenge(false)}
          inboxId={selectedInbox.id}
        />
      )}
    </div>
  );
};

export default Settings;
